<script setup>
// A linha do tempo de uma pessoa: cada disparo que saiu para ela, agrupado por
// dia, do mais recente para o mais antigo.
//
// Cada entrada carrega o POST que foi para a Meta e a resposta crua. É o que
// permite responder "chegou?" sem abrir o banco — e, quando não chegou, dizer
// se foi a Meta que recusou (ERRO) ou se nem tentamos (PULADO, com motivo).
import { computed, onMounted, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import { useStore } from "dashboard/composables/store";
import { useAlert } from "dashboard/composables";
import outboundsAPI from "dashboard/api/outbounds";
import {
  fullTimestamp,
  groupByDay,
  statusTone,
  timeLabel,
} from "../helpers/outboundHistory";

const props = defineProps({
  pessoa: { type: String, required: true },
  summary: { type: Object, default: () => ({}) },
});

const emit = defineEmits(["back", "open-conversation"]);

const { t } = useI18n();
const store = useStore();
const accountId = store.getters.getCurrentAccountId;

const posts = ref([]);
const loading = ref(false);
const expanded = ref(null);
// A conversa vem do detalhe, que a resolve pelo telefone do disparo mais
// recente. O resumo da lista serve de ponte até essa resposta chegar.
const conversa = ref(null);

const conversation = computed(() => conversa.value || props.summary.conversa);

const groups = computed(() => groupByDay(posts.value, t));

const toneClasses = {
  teal: "bg-n-teal-3 text-n-teal-11",
  ruby: "bg-n-ruby-3 text-n-ruby-11",
  amber: "bg-n-amber-3 text-n-amber-11",
};

async function load() {
  loading.value = true;
  try {
    const payload = await outboundsAPI.historyPerson(accountId, props.pessoa);
    posts.value = payload?.disparos || [];
    conversa.value = payload?.conversa || null;
  } catch (error) {
    useAlert(error?.response?.data?.error || t("OUTBOUNDS.HISTORY.LOAD_ERROR"));
  } finally {
    loading.value = false;
  }
}

// JSON.stringify com indentação: o corpo é lido por gente, não por parser.
function pretty(value) {
  if (value === null || value === undefined) return "—";
  try {
    return JSON.stringify(value, null, 2);
  } catch (error) {
    return String(value);
  }
}

function toggle(id) {
  expanded.value = expanded.value === id ? null : id;
}

watch(() => props.pessoa, load);
onMounted(load);
</script>

<template>
  <div class="flex flex-col gap-4">
    <div class="flex items-start justify-between gap-3">
      <div>
        <button
          type="button"
          class="flex items-center gap-1 mb-2 text-sm text-n-slate-11 hover:text-n-slate-12"
          @click="$emit('back')"
        >
          <i class="i-lucide-arrow-left size-4" />
          {{ t("OUTBOUNDS.HISTORY.BACK") }}
        </button>
        <h2 class="text-lg font-medium text-n-slate-12">
          {{ summary.nome || t("OUTBOUNDS.HISTORY.NO_NAME") }}
        </h2>
        <p class="text-sm text-n-slate-10">
          <span class="tabular-nums">{{ summary.phone || pessoa }}</span>
          <span v-if="summary.email"> · {{ summary.email }}</span>
        </p>
      </div>
      <div class="flex flex-col items-end gap-2 shrink-0">
        <button
          v-if="conversation && conversation.id"
          type="button"
          class="flex items-center gap-1 text-sm text-n-brand hover:underline"
          @click="
            emit('open-conversation', {
              nome: summary.nome,
              conversa: conversation,
            })
          "
        >
          <i class="i-lucide-messages-square size-4" />
          {{ t("OUTBOUNDS.HISTORY.CONVERSATION.OPEN") }}
        </button>
        <span v-else class="text-xs text-n-slate-10">
          {{ t("OUTBOUNDS.HISTORY.CONVERSATION.NONE") }}
        </span>

        <div class="flex gap-1">
          <span
            v-if="summary.enviados"
            class="px-2 py-0.5 text-xs rounded-full bg-n-teal-3 text-n-teal-11 tabular-nums"
          >
            {{
              t(
                "OUTBOUNDS.HISTORY.SENT_N",
                { n: summary.enviados },
                summary.enviados,
              )
            }}
          </span>
          <span
            v-if="summary.erros"
            class="px-2 py-0.5 text-xs rounded-full bg-n-ruby-3 text-n-ruby-11 tabular-nums"
          >
            {{
              t(
                "OUTBOUNDS.HISTORY.FAILED_N",
                { n: summary.erros },
                summary.erros,
              )
            }}
          </span>
          <span
            v-if="summary.pulados"
            class="px-2 py-0.5 text-xs rounded-full bg-n-amber-3 text-n-amber-11 tabular-nums"
          >
            {{
              t(
                "OUTBOUNDS.HISTORY.SKIPPED_N",
                { n: summary.pulados },
                summary.pulados,
              )
            }}
          </span>
        </div>
      </div>
    </div>

    <p v-if="loading" class="text-sm text-n-slate-10">
      {{ t("OUTBOUNDS.HISTORY.LOADING") }}
    </p>

    <p v-else-if="!posts.length" class="text-sm text-n-slate-10">
      {{ t("OUTBOUNDS.HISTORY.PERSON_EMPTY") }}
    </p>

    <div v-else class="flex flex-col gap-4">
      <div
        v-for="group in groups"
        :key="group.label"
        class="flex flex-col gap-2"
      >
        <h3 class="text-xs font-medium tracking-wide uppercase text-n-slate-10">
          {{ group.label }}
        </h3>

        <div
          v-for="post in group.items"
          :key="post.id"
          class="border rounded-lg border-n-weak"
        >
          <div class="flex flex-wrap items-center gap-3 px-4 py-3">
            <span
              class="px-2 py-0.5 text-xs rounded-full"
              :class="toneClasses[statusTone(post.status)]"
            >
              {{ post.status }}
            </span>
            <span
              class="text-sm tabular-nums text-n-slate-12"
              :title="fullTimestamp(post.sent_at)"
            >
              {{ timeLabel(post.sent_at) }}
            </span>
            <span class="text-sm text-n-slate-12">{{
              post.template_name
            }}</span>
            <span v-if="post.segmento" class="text-xs text-n-slate-10">
              {{ post.segmento }}
            </span>
            <span class="text-xs text-n-slate-10">
              · {{ post.chatbot_name || `#${post.chatbot_id}` }}
            </span>
            <span
              v-if="post.conversa && post.conversa.entrega"
              class="text-xs text-n-slate-10"
            >
              ·
              {{
                t(
                  `OUTBOUNDS.HISTORY.DELIVERY.${post.conversa.entrega.toUpperCase()}`,
                )
              }}
            </span>
            <button
              v-if="post.conversa && post.conversa.id"
              type="button"
              class="flex items-center gap-1 ml-auto text-xs text-n-brand hover:underline"
              @click="
                emit('open-conversation', {
                  nome: summary.nome,
                  conversa: post.conversa,
                })
              "
            >
              <i class="i-lucide-messages-square size-4" />
              {{
                post.conversa.inbox_name ||
                t("OUTBOUNDS.HISTORY.CONVERSATION.OPEN")
              }}
            </button>
            <button
              type="button"
              class="text-xs text-n-slate-11 hover:text-n-slate-12"
              :class="{ 'ml-auto': !(post.conversa && post.conversa.id) }"
              @click="toggle(post.id)"
            >
              {{
                expanded === post.id
                  ? t("OUTBOUNDS.HISTORY.HIDE_JSON")
                  : t("OUTBOUNDS.HISTORY.SHOW_JSON")
              }}
            </button>
          </div>

          <p v-if="post.erro" class="px-4 pb-3 text-xs text-n-ruby-11">
            {{ post.erro }}
          </p>

          <div v-if="expanded === post.id" class="px-4 pb-4">
            <dl class="grid grid-cols-2 gap-2 mb-3 text-xs text-n-slate-11">
              <div>
                <dt class="text-n-slate-10">
                  {{ t("OUTBOUNDS.HISTORY.FIELDS.LOTE") }}
                </dt>
                <dd class="break-all">{{ post.id_lote }}</dd>
              </div>
              <div>
                <dt class="text-n-slate-10">
                  {{ t("OUTBOUNDS.HISTORY.FIELDS.MSG_ID") }}
                </dt>
                <dd class="break-all">{{ post.msg_id || "—" }}</dd>
              </div>
              <div>
                <dt class="text-n-slate-10">
                  {{ t("OUTBOUNDS.HISTORY.FIELDS.ENDPOINT") }}
                </dt>
                <dd class="break-all">
                  {{ post.endpoint || "—" }}
                  <span v-if="post.http_status" class="tabular-nums">
                    ({{ post.http_status }})
                  </span>
                </dd>
              </div>
              <div>
                <dt class="text-n-slate-10">
                  {{ t("OUTBOUNDS.HISTORY.FIELDS.AGENT") }}
                </dt>
                <dd class="break-all">
                  {{ post.agent_phone || "—" }}
                </dd>
              </div>
            </dl>

            <h4 class="mb-1 text-xs font-medium text-n-slate-11">
              {{ t("OUTBOUNDS.HISTORY.FIELDS.REQUEST") }}
            </h4>
            <pre
              class="p-3 mb-3 overflow-x-auto text-xs rounded-lg bg-n-alpha-1 text-n-slate-12"
              >{{ pretty(post.request) }}</pre>

            <h4 class="mb-1 text-xs font-medium text-n-slate-11">
              {{ t("OUTBOUNDS.HISTORY.FIELDS.RESPONSE") }}
            </h4>
            <pre
              class="p-3 overflow-x-auto text-xs rounded-lg bg-n-alpha-1 text-n-slate-12"
              >{{ pretty(post.response) }}</pre>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
